import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { persist } from "zustand/middleware";
import type {
  Tenant, Order, OrderStatus, Product, ProductStatus,
  Seller, Cart, CartItem, Customer, Company,
  Quote, QuoteStatus, PurchaseOrder, PosShift, PosCommand,
  Payout, Rma, RmaStatus, StockTransfer,
  Notification, AuditEvent, PaymentStatus, FulfillmentStatus,
} from "../core/types";
import type { UserRole } from "../core/permissions";
import {
  TENANTS, PRODUCTS, ORDERS, SELLERS, CUSTOMERS, COMPANIES, QUOTES,
  PURCHASE_ORDERS, POS_SHIFTS, PAYOUTS, RMAS, STOCK_TRANSFERS,
  NOTIFICATIONS, AUDIT_EVENTS, ACTIVE_CART,
} from "../mocks/seed";

// ---- Demo scenario (role switcher) ----
export interface DemoScenario {
  id: string;
  label: string;
  sublabel: string;
  role: UserRole;
  tenantId?: string;
  sellerId?: string;
  companyId?: string;
  outletId?: string;
  userId: string;
  userName: string;
  userEmail: string;
  surface: "platform" | "merchant" | "seller" | "storefront" | "pos" | "warehouse" | "b2b";
  networkState: "online" | "offline";
}

export const DEMO_SCENARIOS: DemoScenario[] = [
  {
    id: "platform_admin",
    label: "Super Admin",
    sublabel: "SYNCORB Platform",
    role: "super_admin",
    userId: "pu_1",
    userName: "Saddam Hussain",
    userEmail: "saddam@syncorb.in",
    surface: "platform",
    networkState: "online",
  },
  {
    id: "merchant_owner",
    label: "Merchant Owner",
    sublabel: "Maharaja Bazaar",
    role: "merchant_owner",
    tenantId: "ten_1",
    userId: "mu_1",
    userName: "Arun Mehta",
    userEmail: "arun@maharajabazaar.com",
    surface: "merchant",
    networkState: "online",
  },
  {
    id: "merchant_2",
    label: "Merchant Owner",
    sublabel: "EliteKart",
    role: "merchant_owner",
    tenantId: "ten_2",
    userId: "mu_3",
    userName: "Rajan Pillai",
    userEmail: "rajan@elitekart.in",
    surface: "merchant",
    networkState: "online",
  },
  {
    id: "seller_1",
    label: "Seller",
    sublabel: "TechZone India",
    role: "seller_owner",
    tenantId: "ten_1",
    sellerId: "seller_1",
    userId: "su_1",
    userName: "Vikram Nair",
    userEmail: "sell@techzone.in",
    surface: "seller",
    networkState: "online",
  },
  {
    id: "seller_2",
    label: "Seller",
    sublabel: "HomeStyle Co.",
    role: "seller_owner",
    tenantId: "ten_1",
    sellerId: "seller_2",
    userId: "su_2",
    userName: "Divya Krishnan",
    userEmail: "sell@homestyle.in",
    surface: "seller",
    networkState: "online",
  },
  {
    id: "customer",
    label: "Customer",
    sublabel: "Rohit Verma",
    role: "customer",
    tenantId: "ten_1",
    userId: "cust_1",
    userName: "Rohit Verma",
    userEmail: "rohit@example.com",
    surface: "storefront",
    networkState: "online",
  },
  {
    id: "b2b_buyer",
    label: "B2B Buyer",
    sublabel: "Infosys Procurement",
    role: "b2b_buyer",
    tenantId: "ten_1",
    companyId: "comp_1",
    userId: "cu_1",
    userName: "Ananya Singh",
    userEmail: "ananya@example.com",
    surface: "b2b",
    networkState: "online",
  },
  {
    id: "cashier_online",
    label: "Cashier",
    sublabel: "Mumbai Flagship — Online",
    role: "cashier",
    tenantId: "ten_1",
    outletId: "out_1",
    userId: "posuser_1",
    userName: "Preethi Kumar",
    userEmail: "preethi@maharajabazaar.com",
    surface: "pos",
    networkState: "online",
  },
  {
    id: "cashier_offline",
    label: "Cashier (Offline)",
    sublabel: "Mumbai Flagship — Offline",
    role: "cashier",
    tenantId: "ten_1",
    outletId: "out_1",
    userId: "posuser_1",
    userName: "Preethi Kumar",
    userEmail: "preethi@maharajabazaar.com",
    surface: "pos",
    networkState: "offline",
  },
  {
    id: "warehouse",
    label: "Warehouse Manager",
    sublabel: "Mumbai Warehouse",
    role: "warehouse_manager",
    tenantId: "ten_1",
    userId: "mu_2",
    userName: "Kavitha Reddy",
    userEmail: "kavitha@maharajabazaar.com",
    surface: "warehouse",
    networkState: "online",
  },
];

export type ColorTheme =
  | "brand"
  | "indigo"
  | "cobalt"
  | "teal"
  | "ember"
  | "violet"
  | "forest"
  | "ocean"
  | "rose"
  | "ink";

export type UiMode = "light" | "dark";

// ---- Store state ----
interface CommerceState {
  // Scenario / session
  scenarioId: string;
  setScenario: (id: string) => void;
  currentScenario: () => DemoScenario;

  // Appearance
  colorTheme: ColorTheme;
  uiMode: UiMode;
  setColorTheme: (theme: ColorTheme) => void;
  setUiMode: (mode: UiMode) => void;

  // Data (all seeded, mutated by actions)
  tenants: Tenant[];
  orders: Order[];
  products: Product[];
  sellers: Seller[];
  customers: Customer[];
  companies: Company[];
  quotes: Quote[];
  purchaseOrders: PurchaseOrder[];
  posShifts: PosShift[];
  posCommands: PosCommand[];
  payouts: Payout[];
  rmas: Rma[];
  stockTransfers: StockTransfer[];
  notifications: Notification[];
  auditEvents: AuditEvent[];
  cart: Cart;

  // Cart actions
  addToCart: (item: Omit<CartItem, "id">) => void;
  removeFromCart: (itemId: string) => void;
  updateCartQty: (itemId: string, qty: number) => void;
  clearCart: () => void;

  // Order actions
  placeOrder: (opts: {
    customerId?: string;
    customerName: string;
    customerEmail: string;
    items?: Array<{ productId: string; productName: string; sku: string; qty: number; unitPrice: number; rowTotal: number; sellerId?: string; sellerName?: string; imageUrl?: string }>;
    shippingAddress?: any;
    paymentMethod?: string;
    subtotal?: number;
    taxTotal?: number;
    shippingTotal?: number;
    grandTotal?: number;
    channelId?: string;
  }) => Order;
  // Order actions
  updateOrderStatus: (
    orderId: string,
    status: OrderStatus,
    meta?: Partial<{
      notes: string;
      cancelReason: string;
      holdReason: string;
      internalNotes: string;
      trackingNumber: string;
      carrier: string;
      inventorySourceId: string;
      packageCount: number;
      estimatedDelivery: string;
      paymentStatus: PaymentStatus;
      fulfillmentStatus: FulfillmentStatus;
    }>
  ) => void;
  updateFulfillmentStatus: (
    orderId: string,
    fulfillmentStatus: FulfillmentStatus,
    meta?: Partial<{
      trackingNumber: string;
      carrier: string;
      inventorySourceId: string;
      packageCount: number;
      estimatedDelivery: string;
      internalNotes: string;
      notes: string;
    }>
  ) => void;

  // Product actions
  updateProductStatus: (productId: string, status: ProductStatus) => void;
  updateProduct: (productId: string, patch: Partial<Product>) => void;

  // Seller actions
  updateSellerStatus: (sellerId: string, status: "pending" | "approved" | "suspended") => void;

  // Payout actions
  updatePayoutStatus: (payoutId: string, status: "requested" | "approved" | "processing" | "paid" | "rejected" | "failed" | "canceled", reference?: string) => void;

  // RMA actions
  updateRmaStatus: (
    rmaId: string,
    status: RmaStatus,
    meta?: Partial<{ resolution: Rma["resolution"]; notes: string; reviewNotes: string; refundAmount: number; declineReason: string }>
  ) => void;

  // Quote actions
  updateQuoteStatus: (quoteId: string, status: QuoteStatus) => void;
  addQuoteMessage: (quoteId: string, msgOrFrom: "buyer" | "seller" | { senderId: string; senderName: string; senderRole: string; message: string }, text?: string) => void;

  // Notification actions
  markNotificationRead: (id: string) => void;
  markAllRead: () => void;

  // POS actions
  openShift: (outletId: string, cashierId: string, cashierName: string, float: number) => PosShift;
  closeShift: (shiftId: string, actualCash?: number) => void;
  addPosCommand: (cmd: Omit<PosCommand, "id" | "status" | "syncedAt">) => PosCommand;
  syncPosCommand: (cmdId: string) => void;
  syncPosCommands: () => void;
  conflictPosCommand: (cmdId: string, reason: string) => void;
  resolveConflict: (cmdId: string) => void;
  resolvePosConflict: (cmdId: string, resolution: "accept" | "discard") => void;

  // Transfer actions
  updateStockTransferStatus: (transferId: string, status: string) => void;

  // Tenant actions
  updateTenantStatus: (tenantId: string, status: string) => void;

  // Audit
  addAudit: (event: Omit<AuditEvent, "id">) => void;

  // Reset all data to seed
  resetToSeed: () => void;
}

const uid = () => Math.random().toString(36).slice(2, 10);

const initialState = () => ({
  scenarioId: "merchant_owner",
  colorTheme: "brand" as ColorTheme,
  uiMode: "light" as UiMode,
  tenants: TENANTS,
  orders: ORDERS,
  products: PRODUCTS,
  sellers: SELLERS,
  customers: CUSTOMERS,
  companies: COMPANIES,
  quotes: QUOTES,
  purchaseOrders: PURCHASE_ORDERS,
  posShifts: POS_SHIFTS,
  posCommands: [] as PosCommand[],
  payouts: PAYOUTS,
  rmas: RMAS,
  stockTransfers: STOCK_TRANSFERS,
  notifications: NOTIFICATIONS,
  auditEvents: AUDIT_EVENTS,
  cart: ACTIVE_CART,
});

export const useStore = create<CommerceState>()(
  persist(
    immer((set, get) => ({
      ...initialState(),

      currentScenario: () => {
        const id = get().scenarioId;
        return DEMO_SCENARIOS.find(s => s.id === id) ?? DEMO_SCENARIOS[0];
      },

      setScenario: (id) => set(s => { s.scenarioId = id; }),

      setColorTheme: (theme) => set(s => { s.colorTheme = theme; }),
      setUiMode: (mode) => set(s => { s.uiMode = mode; }),

      // ---- Cart ----
      addToCart: (item) => set(s => {
        const lineTotal = item.unitPrice * item.qty;
        const existing = s.cart.items.find(i => i.productId === item.productId && i.sellerId === item.sellerId);
        if (existing) {
          existing.qty += item.qty;
          existing.lineTotal = existing.unitPrice * existing.qty;
        } else {
          s.cart.items.push({ ...item, id: uid(), lineTotal });
        }
        s.cart.subtotal = s.cart.items.reduce((a, i) => a + (i.lineTotal ?? i.unitPrice * i.qty), 0);
        s.cart.grandTotal = s.cart.subtotal + (s.cart.shippingAmount ?? 0) + (s.cart.taxAmount ?? 0) - (s.cart.discountAmount ?? 0);
      }),

      removeFromCart: (itemId) => set(s => {
        s.cart.items = s.cart.items.filter(i => i.id !== itemId);
        s.cart.subtotal = s.cart.items.reduce((a, i) => a + (i.lineTotal ?? i.unitPrice * i.qty), 0);
        s.cart.grandTotal = s.cart.subtotal + (s.cart.shippingAmount ?? 0) + (s.cart.taxAmount ?? 0) - (s.cart.discountAmount ?? 0);
      }),

      updateCartQty: (itemId, qty) => set(s => {
        const item = s.cart.items.find(i => i.id === itemId);
        if (item) { item.qty = qty; item.lineTotal = item.unitPrice * qty; }
        s.cart.subtotal = s.cart.items.reduce((a, i) => a + (i.lineTotal ?? i.unitPrice * i.qty), 0);
        s.cart.grandTotal = s.cart.subtotal + (s.cart.shippingAmount ?? 0) + (s.cart.taxAmount ?? 0) - (s.cart.discountAmount ?? 0);
      }),


      clearCart: () => set(s => { s.cart.items = []; s.cart.subtotal = 0; s.cart.grandTotal = 0; }),

      // ---- Orders ----
      placeOrder: (opts) => {
        const cart = get().cart;
        const uid2 = () => Math.random().toString(36).slice(2, 10);
        const orderItems = opts.items
          ? opts.items.map(i => ({
              id: uid2(),
              productId: i.productId,
              productName: i.productName,
              sku: i.sku,
              sellerId: i.sellerId,
              sellerName: i.sellerName,
              offerId: undefined,
              qty: i.qty,
              qtyInvoiced: 0,
              qtyShipped: 0,
              qtyRefunded: 0,
              unitPrice: i.unitPrice,
              taxAmount: Math.round(i.rowTotal * 0.18),
              discountAmount: 0,
              lineTotal: i.rowTotal,
              rowTotal: i.rowTotal,
              fulfillmentStatus: "unallocated" as const,
              imageUrl: i.imageUrl,
            }))
          : cart.items.map(ci => ({
              id: uid2(),
              productId: ci.productId,
              productName: ci.productName,
              sku: ci.sku,
              sellerId: ci.sellerId,
              sellerName: ci.sellerName,
              offerId: ci.offerId,
              qty: ci.qty,
              qtyInvoiced: 0,
              qtyShipped: 0,
              qtyRefunded: 0,
              unitPrice: ci.unitPrice,
              taxAmount: Math.round((ci.lineTotal ?? ci.unitPrice * ci.qty) * 0.18),
              discountAmount: 0,
              lineTotal: ci.lineTotal ?? ci.unitPrice * ci.qty,
              rowTotal: ci.lineTotal ?? ci.unitPrice * ci.qty,
              fulfillmentStatus: "unallocated" as const,
              imageUrl: ci.imageUrl,
            }));
        const subtotal = opts.subtotal ?? cart.subtotal;
        const taxAmount = opts.taxTotal ?? (cart.taxAmount ?? Math.round(subtotal * 0.18));
        const shippingAmount = opts.shippingTotal ?? (cart.shippingAmount ?? 0);
        const grandTotal = opts.grandTotal ?? (cart.grandTotal ?? subtotal + taxAmount + shippingAmount);
        const defaultAddr = CUSTOMERS[0]?.addresses?.[0] ?? { line1: "—", city: "Mumbai", state: "MH", pincode: "400001", country: "IN" };
        const newOrder: Order = {
          id: `ord_${uid()}`,
          tenantId: cart.tenantId ?? "ten_1",
          channelId: opts.channelId ?? cart.channelId ?? "ch_web",
          number: `ORD-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
          customerId: opts.customerId ?? "guest",
          customerName: opts.customerName,
          customerEmail: opts.customerEmail,
          billingAddress: opts.shippingAddress ?? defaultAddr,
          shippingAddress: opts.shippingAddress ?? defaultAddr,
          items: orderItems,
          status: "pending" as const,
          paymentStatus: "captured" as const,
          fulfillmentStatus: "unallocated" as const,
          paymentMethod: opts.paymentMethod ?? "COD",
          subtotal,
          shippingAmount: shippingAmount, // required field
          taxAmount: taxAmount,           // required field
          discountAmount: cart.discountAmount ?? 0, // required field
          shippingTotal: shippingAmount,
          taxTotal: taxAmount,
          discountTotal: cart.discountAmount ?? 0,
          grandTotal,
          source: opts.channelId === "pos" ? "pos" : "web",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set(s => {
          s.orders.unshift(newOrder);
          s.cart.items = [];
          s.cart.subtotal = 0;
          s.cart.grandTotal = 0;
        });
        return newOrder;
      },

      updateOrderStatus: (orderId, status, meta) => set(s => {
        const o = s.orders.find(o => o.id === orderId);
        if (!o) return;
        o.status = status;
        o.updatedAt = new Date().toISOString();
        if (meta?.notes !== undefined) o.notes = meta.notes;
        if (meta?.cancelReason !== undefined) o.cancelReason = meta.cancelReason;
        if (meta?.holdReason !== undefined) o.holdReason = meta.holdReason;
        if (meta?.internalNotes !== undefined) o.internalNotes = meta.internalNotes;
        if (meta?.trackingNumber !== undefined) o.trackingNumber = meta.trackingNumber;
        if (meta?.carrier !== undefined) o.carrier = meta.carrier;
        if (meta?.inventorySourceId !== undefined) o.inventorySourceId = meta.inventorySourceId;
        if (meta?.packageCount !== undefined) o.packageCount = meta.packageCount;
        if (meta?.estimatedDelivery !== undefined) o.estimatedDelivery = meta.estimatedDelivery;
        if (meta?.paymentStatus !== undefined) o.paymentStatus = meta.paymentStatus;
        if (meta?.fulfillmentStatus !== undefined) o.fulfillmentStatus = meta.fulfillmentStatus;
        if (status === "completed" && !o.fulfillmentStatus.startsWith("deliver")) {
          if (!meta?.fulfillmentStatus) o.fulfillmentStatus = o.trackingNumber || meta?.trackingNumber ? "shipped" : "packed";
        }
        if (status === "canceled") o.fulfillmentStatus = "canceled";
        if (status === "processing" && o.fulfillmentStatus === "unallocated" && meta?.inventorySourceId) {
          o.fulfillmentStatus = "allocated";
        }
      }),

      updateFulfillmentStatus: (orderId, fulfillmentStatus, meta) => set(s => {
        const o = s.orders.find(o => o.id === orderId);
        if (!o) return;
        o.fulfillmentStatus = fulfillmentStatus;
        o.updatedAt = new Date().toISOString();
        if (meta?.trackingNumber !== undefined) o.trackingNumber = meta.trackingNumber;
        if (meta?.carrier !== undefined) o.carrier = meta.carrier;
        if (meta?.inventorySourceId !== undefined) o.inventorySourceId = meta.inventorySourceId;
        if (meta?.packageCount !== undefined) o.packageCount = meta.packageCount;
        if (meta?.estimatedDelivery !== undefined) o.estimatedDelivery = meta.estimatedDelivery;
        if (meta?.internalNotes !== undefined) o.internalNotes = meta.internalNotes;
        if (meta?.notes !== undefined) o.notes = meta.notes;
        if (fulfillmentStatus === "shipped") {
          o.shippedAt = new Date().toISOString();
          if (o.status === "pending" || o.status === "processing") o.status = "processing";
        }
        if (fulfillmentStatus === "delivered") {
          o.deliveredAt = new Date().toISOString();
          o.status = "completed";
        }
        if (fulfillmentStatus === "canceled") o.status = "canceled";
        o.items.forEach(item => { item.fulfillmentStatus = fulfillmentStatus; });
      }),

      updateProductStatus: (productId, status) => set(s => {
        const p = s.products.find(p => p.id === productId);
        if (p) { p.status = status; p.updatedAt = new Date().toISOString(); }
      }),

      updateProduct: (productId, patch) => set(s => {
        const p = s.products.find(p => p.id === productId);
        if (!p) return;
        Object.assign(p, patch, { updatedAt: new Date().toISOString() });
      }),

      updateSellerStatus: (sellerId, status) => set(s => {
        const sel = s.sellers.find(s => s.id === sellerId);
        if (sel) sel.status = status;
      }),

      updatePayoutStatus: (payoutId, status, reference) => set(s => {
        const p = s.payouts.find(p => p.id === payoutId);
        if (p) {
          p.status = status;
          if (reference) p.reference = reference;
          if (status === "paid") { p.releasedAt = new Date().toISOString(); p.releasedBy = "mu_1"; }
        }
      }),

      updateRmaStatus: (rmaId, status, meta) => set(s => {
        const r = s.rmas.find(r => r.id === rmaId);
        if (!r) return;
        r.status = status;
        r.updatedAt = new Date().toISOString();
        if (meta?.resolution !== undefined) r.resolution = meta.resolution;
        if (meta?.notes !== undefined) r.notes = meta.notes;
        if (meta?.reviewNotes !== undefined) r.reviewNotes = meta.reviewNotes;
        if (meta?.refundAmount !== undefined) r.refundAmount = meta.refundAmount;
        if (meta?.declineReason !== undefined) r.declineReason = meta.declineReason;
      }),

      updateQuoteStatus: (quoteId, status) => set(s => {
        const q = s.quotes.find(q => q.id === quoteId);
        if (q) { q.status = status; q.updatedAt = new Date().toISOString(); }
      }),

      addQuoteMessage: (quoteId, msgOrFrom, text) => set(s => {
        const q = s.quotes.find(q => q.id === quoteId);
        if (!q) return;
        if (!q.messages) q.messages = [];
        if (typeof msgOrFrom === "object") {
          q.messages.push({ id: uid(), ...msgOrFrom, createdAt: new Date().toISOString() });
        } else {
          q.messages.push({ id: uid(), from: msgOrFrom, senderRole: msgOrFrom, message: text ?? "", createdAt: new Date().toISOString() });
        }
      }),

      markNotificationRead: (id) => set(s => {
        const n = s.notifications.find(n => n.id === id);
        if (n) { n.read = true; n.isRead = true; }
      }),

      markAllRead: () => set(s => { s.notifications.forEach(n => { n.read = true; n.isRead = true; }); }),

      openShift: (outletId, cashierId, cashierName, float) => {
        const shift: PosShift = {
          id: `shift_${uid()}`,
          outletId, cashierId, cashierName,
          status: "open",
          openingFloat: float,
          cashSales: 0, cashRefunds: 0,
          expectedCash: float,
          grossSales: 0, netSales: 0,
          cashIn: float, cashOut: 0,
          openedAt: new Date().toISOString(),
          orders: [],
        };
        set(s => { s.posShifts.push(shift); });
        return shift;
      },

      closeShift: (shiftId, actualCash) => set(s => {
        const shift = s.posShifts.find(sh => sh.id === shiftId);
        if (shift) {
          shift.status = "closed";
          shift.closedAt = new Date().toISOString();
          if (actualCash !== undefined) {
            shift.actualCash = actualCash;
            shift.variance = actualCash - shift.expectedCash;
          }
        }
      }),

      addPosCommand: (cmd) => {
        const command: PosCommand = { ...cmd, id: `cmd_${uid()}`, status: "queued" };
        set(s => { s.posCommands.push(command); });
        return command;
      },

      syncPosCommand: (cmdId) => set(s => {
        const cmd = s.posCommands.find(c => c.id === cmdId);
        if (cmd) { cmd.status = "synced"; cmd.syncedAt = new Date().toISOString(); }
      }),

      conflictPosCommand: (cmdId, reason) => set(s => {
        const cmd = s.posCommands.find(c => c.id === cmdId);
        if (cmd) { cmd.status = "conflict"; cmd.conflictReason = reason; }
      }),

      resolveConflict: (cmdId) => set(s => {
        const cmd = s.posCommands.find(c => c.id === cmdId);
        if (cmd) { cmd.status = "resolved"; }
      }),

      syncPosCommands: () => set(s => {
        s.posCommands.forEach(cmd => {
          if (cmd.status === "queued") { cmd.status = "synced"; cmd.syncedAt = new Date().toISOString(); }
        });
      }),

      resolvePosConflict: (cmdId, resolution) => set(s => {
        const cmd = s.posCommands.find(c => c.id === cmdId);
        if (cmd) { cmd.status = resolution === "accept" ? "synced" : "resolved"; }
      }),

      updateStockTransferStatus: (transferId, status) => set(s => {
        const t = s.stockTransfers.find(t => t.id === transferId);
        if (t) { t.status = status as any; t.updatedAt = new Date().toISOString(); }
      }),

      updateTenantStatus: (tenantId, status) => set(s => {
        const t = s.tenants.find(t => t.id === tenantId);
        if (t) t.status = status as Tenant["status"];
      }),

      addAudit: (event) => set(s => {
        s.auditEvents.unshift({ ...event, id: `ae_${uid()}`, at: event.at ?? new Date().toISOString() });
      }),

      resetToSeed: () => set((state) => ({
        ...initialState(),
        colorTheme: state.colorTheme,
        uiMode: state.uiMode,
      })),
    })),
    {
      name: "syncorb-commerce-mock",
      partialize: (s) => ({
        scenarioId: s.scenarioId,
        colorTheme: s.colorTheme,
        uiMode: s.uiMode,
        orders: s.orders,
        cart: s.cart,
        sellers: s.sellers,
        posShifts: s.posShifts,
        posCommands: s.posCommands,
        notifications: s.notifications,
        payouts: s.payouts,
        quotes: s.quotes,
        rmas: s.rmas,
      }),
    }
  )
);

// Convenience selectors — never return a freshly allocated array/object from
// the zustand selector itself (v5 useSyncExternalStore loops on that).
export const useScenario = () => useStore(s => s.currentScenario());
export const useTenantOrders = (tenantId: string) => {
  const orders = useStore(s => s.orders);
  return orders.filter(o => o.tenantId === tenantId);
};
export const useTenantProducts = (tenantId: string) => {
  const products = useStore(s => s.products);
  return products.filter(p => p.tenantId === tenantId);
};
export const useTenantSellers = (tenantId: string) => {
  const sellers = useStore(s => s.sellers);
  return sellers.filter(s => s.tenantId === tenantId);
};
export const useSellerOrders = (sellerId: string) => {
  const orders = useStore(s => s.orders);
  return orders.filter(o => o.items.some(i => i.sellerId === sellerId));
};
export const useUnreadCount = () => useStore(s => s.notifications.filter(n => !n.read).length);
export const useCurrentShift = (outletId: string) => useStore(s => s.posShifts.find(sh => sh.outletId === outletId && sh.status === "open"));
export const usePendingCommands = () => {
  const commands = useStore(s => s.posCommands);
  return commands.filter(c => c.status === "queued" || c.status === "conflict");
};
